/**
 * @fileoverview Monitor detail page component.
 * Displays comprehensive monitor information including configuration,
 * uptime statistics, response time charts, and recent check history.
 * Supports inline editing and manual check triggering.
 * @module pages/monitor-detail
 */

import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth.js";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/authUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, ChevronLeft, Clock, AlertCircle, CheckCircle2, Edit2, AlertTriangle, Play, Loader2 } from "lucide-react";
import { Link, useRoute } from "wouter";
import { insertMonitorSchema, type Monitor, type CheckResult, type InsertMonitor } from "@shared/schema";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { MonitorForm } from "@/components/MonitorForm";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { z } from "zod";

/** Extended form schema with headers as string for JSON input */
const formSchema = insertMonitorSchema.extend({
  headers: z.string().optional(),
});

/** Form data type inferred from formSchema */
type FormData = z.infer<typeof formSchema>;

/**
 * Monitor detail page component.
 * Protected page showing full monitor details with:
 * - Configuration display and inline editing
 * - Uptime percentage and check counts
 * - Response time chart (last 50 checks)
 * - Recent check history with status indicators
 * - Manual check trigger button
 * @returns Monitor detail page JSX
 */
export default function MonitorDetail() {
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, params] = useRoute("/monitor/:id");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  const { data: monitor, isLoading: monitorLoading } = useQuery<Monitor>({
    queryKey: ["/api/monitors", params?.id],
    enabled: isAuthenticated && !!params?.id,
  });

  const { data: checkResults, isLoading: resultsLoading } = useQuery<CheckResult[]>({
    queryKey: ["/api/monitors", params?.id, "checks"],
    enabled: isAuthenticated && !!params?.id,
    refetchInterval: 30000,
  });

  const { data: uptime } = useQuery<{ percentage: number; totalChecks: number; successfulChecks: number }>({
    queryKey: ["/api/monitors", params?.id, "uptime"],
    enabled: isAuthenticated && !!params?.id,
  });

  const updateMonitorMutation = useMutation({
    mutationFn: async (data: FormData) => {
      let headers = undefined;
      if (data.headers && data.headers.trim()) {
        try {
          headers = JSON.parse(data.headers);
        } catch {
          throw new Error("Invalid JSON in headers");
        }
      }

      const monitorData: Partial<InsertMonitor> = {
        name: data.name,
        url: data.url,
        method: data.method,
        interval: data.interval,
        headers,
        body: data.body || undefined,
        alertStatusCodes: data.alertStatusCodes,
        isActive: data.isActive,
      };

      await apiRequest("PATCH", `/api/monitors/${params?.id}`, monitorData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitors", params?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitors"] });
      toast({
        title: "Success",
        description: "Monitor updated successfully",
      });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: error.message || "Failed to update monitor",
        variant: "destructive",
      });
    },
  });

  const runCheckMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/monitors/${params?.id}/check`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitors", params?.id, "checks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitors", params?.id, "uptime"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitors/stats"] });
      toast({
        title: "Check Complete",
        description: "Health check executed successfully",
      });
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: error.message || "Failed to run health check",
        variant: "destructive",
      });
    },
  });

  /**
   * Handles form submission for monitor updates.
   * @param data - Updated form data
   */
  const handleUpdate = (data: FormData) => {
    updateMonitorMutation.mutate(data);
  };

  /**
   * Handles cancel button in edit mode.
   */
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  if (authLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-background" />;
  }

  const chartData = checkResults
    ?.slice(-50)
    .reverse()
    .map((result) => ({
      time: format(new Date(result.checked), "HH:mm"),
      latency: result.responseTime || 0,
      success: result.success,
    })) || [];

  const recentChecks = checkResults?.slice(0, 20) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="monitor-detail-page">
      <header className="border-b sticky top-0 bg-background z-50" data-testid="monitor-detail-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-testid="monitor-detail-navbar-container">
          <Link href="/" className="flex items-center gap-2" data-testid="monitor-detail-navbar-home-link">
            <Activity className="h-6 w-6 text-primary" data-testid="monitor-detail-navbar-logo-icon" />
            <span className="text-lg font-semibold" data-testid="monitor-detail-navbar-brand-text">Clockwork</span>
          </Link>
          <a href="/api/logout" data-testid="monitor-detail-navbar-logout-link">
            <Button variant="ghost" data-testid="monitor-detail-navbar-logout-button">Log Out</Button>
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="monitor-detail-main-content">
        <Link href="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="monitor-detail-back-link">
          <ChevronLeft className="h-4 w-4 mr-1" data-testid="monitor-detail-back-icon" />
          Back to Dashboard
        </Link>

        {monitorLoading ? (
          <div className="space-y-6" data-testid="monitor-detail-loading-state">
            <Skeleton className="h-12 w-3/4" data-testid="monitor-detail-loading-header" />
            <Skeleton className="h-64 w-full" data-testid="monitor-detail-loading-content" />
          </div>
        ) : monitor ? (
          <div className="space-y-6" data-testid="monitor-detail-content">
            {isEditing ? (
              <Card data-testid="monitor-detail-edit-card">
                <CardHeader data-testid="monitor-detail-edit-card-header">
                  <CardTitle data-testid="monitor-detail-edit-card-title">Edit Monitor</CardTitle>
                </CardHeader>
                <CardContent data-testid="monitor-detail-edit-card-content">
                  <MonitorForm
                    initialValues={{
                      name: monitor.name,
                      url: monitor.url,
                      method: monitor.method as "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
                      interval: monitor.interval,
                      headers: monitor.headers as any,
                      body: monitor.body || undefined,
                      alertStatusCodes: monitor.alertStatusCodes,
                      isActive: monitor.isActive,
                    }}
                    onSubmit={handleUpdate}
                    submitLabel="Save Changes"
                    isPending={updateMonitorMutation.isPending}
                    onCancel={handleCancelEdit}
                  // Pass a base testid for the form to inherit
                  // Note: MonitorForm needs to be updated to accept a base testid prop if we want strict inheritance,
                  // but for now we rely on its internal unique IDs.
                  />
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4" data-testid="monitor-detail-header-container">
                  <div data-testid="monitor-detail-header-info">
                    <div className="flex items-center gap-3 mb-2" data-testid="monitor-detail-header-title-row">
                      <h1 className="text-2xl font-semibold" data-testid="monitor-detail-header-title">
                        {monitor.name}
                      </h1>
                      <Badge variant={monitor.isActive ? "default" : "secondary"} data-testid="monitor-detail-header-status-badge">
                        {monitor.isActive ? "Active" : "Paused"}
                      </Badge>
                    </div>
                    <p className="text-sm font-mono text-muted-foreground break-all" data-testid="monitor-detail-header-url">
                      {monitor.url}
                    </p>
                  </div>
                  <div className="flex items-center gap-2" data-testid="monitor-detail-header-actions">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => runCheckMutation.mutate()}
                      disabled={runCheckMutation.isPending}
                      data-testid="monitor-detail-header-run-check-button"
                    >
                      {runCheckMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" data-testid="monitor-detail-header-run-check-loading-icon" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" data-testid="monitor-detail-header-run-check-icon" />
                      )}
                      Run Check Now
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      data-testid="monitor-detail-header-edit-button"
                    >
                      <Edit2 className="h-4 w-4 mr-2" data-testid="monitor-detail-header-edit-icon" />
                      Edit
                    </Button>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3" data-testid="monitor-detail-stats-grid">
                  <Card data-testid="monitor-detail-stats-uptime-card">
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="monitor-detail-stats-uptime-header">
                      <h3 className="text-sm font-medium text-muted-foreground" data-testid="monitor-detail-stats-uptime-title">Uptime</h3>
                    </CardHeader>
                    <CardContent data-testid="monitor-detail-stats-uptime-content">
                      {uptime ? (
                        <div className="text-2xl font-semibold" data-testid="monitor-detail-stats-uptime-value">
                          {uptime.percentage.toFixed(2)}%
                        </div>
                      ) : (
                        <Skeleton className="h-8 w-20" data-testid="monitor-detail-stats-uptime-skeleton" />
                      )}
                      {uptime && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid="monitor-detail-stats-uptime-subtext">
                          {uptime.successfulChecks} / {uptime.totalChecks} checks
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card data-testid="monitor-detail-stats-interval-card">
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="monitor-detail-stats-interval-header">
                      <h3 className="text-sm font-medium text-muted-foreground" data-testid="monitor-detail-stats-interval-title">Check Interval</h3>
                    </CardHeader>
                    <CardContent data-testid="monitor-detail-stats-interval-content">
                      <div className="text-2xl font-semibold" data-testid="monitor-detail-stats-interval-value">
                        {monitor.interval < 60000
                          ? `${Math.floor(monitor.interval / 1000)}s`
                          : monitor.interval < 3600000
                            ? `${Math.floor(monitor.interval / 60000)}m`
                            : `${Math.floor(monitor.interval / 3600000)}h`}
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="monitor-detail-stats-method-card">
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="monitor-detail-stats-method-header">
                      <h3 className="text-sm font-medium text-muted-foreground" data-testid="monitor-detail-stats-method-title">Method</h3>
                    </CardHeader>
                    <CardContent data-testid="monitor-detail-stats-method-content">
                      <div className="text-2xl font-semibold font-mono" data-testid="monitor-detail-stats-method-value">
                        {monitor.method}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card data-testid="monitor-detail-chart-card">
                  <CardHeader data-testid="monitor-detail-chart-header">
                    <CardTitle data-testid="monitor-detail-chart-title">Response Time (Last 50 Checks)</CardTitle>
                  </CardHeader>
                  <CardContent data-testid="monitor-detail-chart-content">
                    {resultsLoading ? (
                      <Skeleton className="h-64 w-full" data-testid="monitor-detail-chart-skeleton" />
                    ) : chartData.length > 0 ? (
                      <div className="h-64" data-testid="monitor-detail-chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis
                              dataKey="time"
                              stroke="hsl(var(--muted-foreground))"
                              fontSize={12}
                            />
                            <YAxis
                              stroke="hsl(var(--muted-foreground))"
                              fontSize={12}
                              label={{ value: 'ms', position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'hsl(var(--popover))',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '6px',
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="latency"
                              stroke="hsl(var(--primary))"
                              strokeWidth={2}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-muted-foreground" data-testid="monitor-detail-chart-empty-state">
                        <div className="text-center">
                          <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" data-testid="monitor-detail-chart-empty-icon" />
                          <p className="text-sm" data-testid="monitor-detail-chart-empty-text">No check data yet</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card data-testid="monitor-detail-checks-card">
                  <CardHeader data-testid="monitor-detail-checks-header">
                    <CardTitle data-testid="monitor-detail-checks-title">Recent Checks</CardTitle>
                  </CardHeader>
                  <CardContent data-testid="monitor-detail-checks-content">
                    {resultsLoading ? (
                      <div className="space-y-2" data-testid="monitor-detail-checks-loading">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Skeleton key={i} className="h-12 w-full" data-testid={`monitor-detail-checks-skeleton-${i}`} />
                        ))}
                      </div>
                    ) : recentChecks.length > 0 ? (
                      <div className="space-y-2" data-testid="monitor-detail-checks-list">
                        {recentChecks.map((check) => (
                          <div
                            key={check.id}
                            className="flex items-center justify-between p-3 border rounded-md"
                            data-testid={`monitor-detail-check-item-${check.id}`}
                          >
                            <div className="flex items-center gap-3 flex-1" data-testid={`monitor-detail-check-item-${check.id}-info`}>
                              {check.isAlert ? (
                                <AlertTriangle className="h-4 w-4 text-amber-500" data-testid={`monitor-detail-check-item-${check.id}-status-icon-alert`} />
                              ) : check.success ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" data-testid={`monitor-detail-check-item-${check.id}-status-icon-success`} />
                              ) : (
                                <AlertCircle className="h-4 w-4 text-destructive" data-testid={`monitor-detail-check-item-${check.id}-status-icon-error`} />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium" data-testid={`monitor-detail-check-item-${check.id}-status-code`}>
                                    {check.statusCode || "N/A"}
                                  </span>
                                  {check.isAlert && (
                                    <Badge variant="outline" className="text-xs border-amber-500 text-amber-600" data-testid={`monitor-detail-check-item-${check.id}-alert-badge`}>
                                      Alert
                                    </Badge>
                                  )}
                                  {check.responseTime && (
                                    <span className="text-xs text-muted-foreground" data-testid={`monitor-detail-check-item-${check.id}-response-time`}>
                                      {check.responseTime}ms
                                    </span>
                                  )}
                                </div>
                                {check.errorMessage && (
                                  <p className="text-xs text-destructive truncate" data-testid={`monitor-detail-check-item-${check.id}-error-message`}>
                                    {check.errorMessage}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground" data-testid={`monitor-detail-check-item-${check.id}-timestamp`}>
                              {format(new Date(check.checked), "MMM d, HH:mm:ss")}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground" data-testid="monitor-detail-checks-empty-state">
                        <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" data-testid="monitor-detail-checks-empty-icon" />
                        <p className="text-sm" data-testid="monitor-detail-checks-empty-text">No checks yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        ) : (
          <Card className="p-12" data-testid="monitor-detail-not-found-card">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" data-testid="monitor-detail-not-found-icon" />
              <h3 className="text-lg font-medium mb-2" data-testid="monitor-detail-not-found-title">Monitor not found</h3>
              <p className="text-sm text-muted-foreground mb-6" data-testid="monitor-detail-not-found-text">
                This monitor does not exist or you don't have access to it
              </p>
              <Link href="/">
                <a data-testid="monitor-detail-not-found-back-link">
                  <Button data-testid="monitor-detail-not-found-back-button">Back to Dashboard</Button>
                </a>
              </Link>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
